/**
 * 旅の期間を編集するシート
 *
 * 期間を変えると訪問・写真・歩数・軌跡はすべてサーバー側で集計し直される。
 */

import { format } from 'date-fns'
import { useEffect, useState } from 'react'

import type { Trip } from '@/api/types/trip'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSubmitLock } from '@/hooks/useSubmitLock'

type Props = {
  trip: Pick<Trip, 'start_at' | 'end_at'>
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (payload: { start_at: string; end_at: string }) => Promise<void>
}

/** datetime-local 入力はローカルタイム表記しか受け付けない */
const toInputValue = (iso: string): string => format(new Date(iso), "yyyy-MM-dd'T'HH:mm")

export const TripPeriodSheet = ({ trip, open, onOpenChange, onSubmit }: Props) => {
  const [startAt, setStartAt] = useState('')
  const [endAt, setEndAt] = useState('')
  const { isSubmitting: isSaving, run } = useSubmitLock()

  useEffect(() => {
    if (!open) return
    setStartAt(toInputValue(trip.start_at))
    setEndAt(toInputValue(trip.end_at))
  }, [open, trip.start_at, trip.end_at])

  const startTime = new Date(startAt).getTime()
  const endTime = new Date(endAt).getTime()
  const isValid = Number.isFinite(startTime) && Number.isFinite(endTime) && endTime > startTime

  const handleSave = async () => {
    if (!isValid) return
    await run(async () => {
      await onSubmit({ start_at: new Date(startTime).toISOString(), end_at: new Date(endTime).toISOString() })
      onOpenChange(false)
    })
  }

  return (
    <BottomSheet title="旅の期間" open={open} onOpenChange={onOpenChange} snapPoints={[0.6]}>
      <div className="space-y-4 px-4 pb-8">
        <div className="space-y-1.5">
          <Label htmlFor="trip-start-at">出発</Label>
          <Input
            id="trip-start-at"
            type="datetime-local"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="trip-end-at">帰宅</Label>
          <Input id="trip-end-at" type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} />
        </div>

        {!isValid && <p className="text-xs text-destructive">帰宅は出発より後の日時にしてください</p>}

        <p className="text-xs text-muted-foreground">
          期間を変えると、含まれる訪問・写真・歩数・ルートが再集計されます。
        </p>

        <Button className="w-full" disabled={!isValid || isSaving} onClick={() => void handleSave()}>
          {isSaving ? '保存中…' : '保存'}
        </Button>
      </div>
    </BottomSheet>
  )
}
